import React, { useState, useEffect } from 'react';
import MainMenu from './components/MainMenu';
import Room from './components/Room';
import GameBoard from './components/GameBoard';
import gameService from './services/gameService';
import useTranslation from './i18n/useTranslation';
import './styles/pixel-art.css';

function App() {
  const { t } = useTranslation();
  const [screen, setScreen] = useState('menu'); // 'menu', 'room', 'game'
  const [roomCode, setRoomCode] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [urlRoomCode, setUrlRoomCode] = useState('');
  const [urlSessionID, setUrlSessionID] = useState('');
  const [isRestoringSession, setIsRestoringSession] = useState(true);

  // Check for existing session and restore on page load
  useEffect(() => {
    console.log('[APP] Checking for session to restore...');
    const params = new URLSearchParams(window.location.search);
    const urlRoom = params.get('room');
    const urlSession = params.get('session');

    // Priority: URL params > localStorage
    let sessionToRestore = null;
    let roomToRestore = null;

    if (urlSession && urlRoom) {
      // URL has both session and room - use these
      sessionToRestore = urlSession;
      roomToRestore = urlRoom.toUpperCase();
      gameService.setSessionID(sessionToRestore);
      gameService.roomCode = roomToRestore;
      console.log('[APP] Restoring session from URL:', { sessionToRestore, roomToRestore });
    } else if (gameService.sessionID && gameService.roomCode) {
      // No URL params but localStorage has session - use that
      sessionToRestore = gameService.sessionID;
      roomToRestore = gameService.roomCode;
      console.log('[APP] Restoring session from localStorage:', { sessionToRestore, roomToRestore });

      // Update URL to include session for future reloads
      updateURL(roomToRestore, sessionToRestore);
    } else if (urlRoom && !urlSession) {
      // Room code in URL but no session - this is an invite link
      console.log('[APP] Invite link detected, room code:', urlRoom.toUpperCase());
      setUrlRoomCode(urlRoom.toUpperCase());
      setIsRestoringSession(false);
      return;
    } else {
      console.log('[APP] No session found in URL or localStorage');
    }

    // If we have a session to restore, set it up for auto-rejoin
    if (sessionToRestore && roomToRestore) {
      console.log('[APP] Session restoration initiated');
      setUrlRoomCode(roomToRestore);
      setUrlSessionID(sessionToRestore);
      setRoomCode(roomToRestore);
    } else {
      // No session to restore, just go to menu
      console.log('[APP] No session to restore, going to menu');
      setIsRestoringSession(false);
    }
  }, []);

  // Update URL with room code and session
  const updateURL = (roomCode, sessionID) => {
    const params = new URLSearchParams();
    if (roomCode) {
      params.set('room', roomCode);
    }
    if (sessionID) {
      params.set('session', sessionID);
    }
    const newURL = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newURL);
  };

  const handleRoomCreated = (code, playerId, link) => {
    setRoomCode(code);
    if (link) {
      // Convert relative path to full URL
      const fullLink = `${window.location.origin}${link}`;
      setInviteLink(fullLink);
    }
    gameService.setPlayerInfo(playerId, gameService.playerName, code);

    // Update URL with session
    updateURL(code, gameService.sessionID);

    // Mark session restoration as complete
    setIsRestoringSession(false);

    setScreen('room');
  };

  const handleRoomJoined = (code, link, gameInProgress) => {
    console.log('[APP] handleRoomJoined called', { code, link, gameInProgress, currentRoomCode: roomCode });

    if (code) {
      setRoomCode(code);
    }
    if (link) {
      // Convert relative path to full URL
      const fullLink = `${window.location.origin}${link}`;
      setInviteLink(fullLink);
    }

    // Update URL with session
    const finalRoomCode = code || roomCode;
    updateURL(finalRoomCode, gameService.sessionID);
    console.log('[APP] Updated URL with room:', finalRoomCode, 'session:', gameService.sessionID);

    // Mark session restoration as complete
    setIsRestoringSession(false);

    // If game is already in progress, go to game screen
    if (gameInProgress) {
      console.log('[APP] Navigating to game screen (game in progress)');
      setScreen('game');
    } else {
      console.log('[APP] Navigating to room screen (lobby)');
      setScreen('room');
    }
  };

  const handleRejoinFailed = () => {
    console.error('[APP] Rejoin failed, clearing session and returning to menu');
    // Clear session and go to menu
    setRoomCode('');
    setUrlRoomCode('');
    setUrlSessionID('');
    gameService.reset();
    window.history.replaceState({}, '', window.location.pathname);
    setIsRestoringSession(false);
    setScreen('menu');
  };

  const handleGameStarted = () => {
    // Update URL when game starts to maintain session
    updateURL(roomCode, gameService.sessionID);
    setScreen('game');
  };

  const handleLeaveRoom = () => {
    setRoomCode('');
    setUrlRoomCode('');
    setUrlSessionID('');
    gameService.reset();
    // Clear URL parameters
    window.history.replaceState({}, '', window.location.pathname);
    setScreen('menu');
  };

  const handleReturnToLobby = () => {
    // Return to room screen while keeping room code and session
    // Update URL to maintain session persistence
    updateURL(roomCode, gameService.sessionID);
    setScreen('room');
  };

  const handleGameOver = () => {
    setRoomCode('');
    setUrlRoomCode('');
    setUrlSessionID('');
    gameService.reset();
    // Clear URL parameters
    window.history.replaceState({}, '', window.location.pathname);
    setScreen('menu');
  };

  return (
    <div className="App">
      {isRestoringSession && urlSessionID && urlRoomCode ? (
        <div className="restoring-session">
          <div className="pixel-text">{t.connection.restoringSession}</div>
        </div>
      ) : null}
      {screen === 'menu' && (
        <MainMenu
          onRoomCreated={handleRoomCreated}
          onRoomJoined={handleRoomJoined}
          onRejoinFailed={handleRejoinFailed}
          urlRoomCode={urlRoomCode}
          urlSessionID={urlSessionID}
        />
      )}
      {screen === 'room' && (
        <Room
          roomCode={roomCode}
          inviteLink={inviteLink}
          onGameStarted={handleGameStarted}
          onLeave={handleLeaveRoom}
        />
      )}
      {screen === 'game' && (
        <GameBoard onGameOver={handleGameOver} onReturnToLobby={handleReturnToLobby} />
      )}
    </div>
  );
}

export default App;
